import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import * as Constants from '@emi/shared/constants';
import {
    ConfigureData,
    DateRange,
    Frequency,
    MinMaxRange,
    PricePer,
    TotalSKUSizeRange,
    VolumeUnits,
    WeightUnits,
} from '@emi/shared/models';
import {
    addConfigureDataSelection,
    addCountRangeSelection,
    addDateRangeSelection,
    addFirstSeenPriceRangeSelection,
    addLastSeenPriceRangeSelection,
    addVolumeRangeSelection,
    addWeightRangeSelection,
    clearFilters,
    DataExtractionState,
    getCountries,
    getSelectedConfigureData,
} from '@emi/shared/state/filters';
import { fetchSkuMetadata, getSkuMetadataStateFeature, SkuMetadataState } from '@emi/shared/state/sku-metadata';
import { convertMetric } from '@emi/shared/utils';
import { select, Store } from '@ngrx/store';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { pairwise, startWith } from 'rxjs/operators';

@Component({
    selector: 'via-filter-tab-configuration',
    templateUrl: './filter-tab-configuration.component.html',
    styleUrls: ['./filter-tab-configuration.component.scss'],
})
export class FilterTabConfigurationComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    public configureData = new ConfigureData();
    public loading = false;
    public frequencyTooltipText = Constants.Tooltips.FREQUENCY;
    public pricePointTooltipText = Constants.Tooltips.PRICE_POINT;
    public firstSeenPriceTooltipText = Constants.Tooltips.FIRST_SEEN_PRICE;
    public dateRangeTooltipText = Constants.Tooltips.DATE_RANGE;
    public lastSeenPriceTooltipText = Constants.Tooltips.LAST_SEEN_PRICE;
    public currencyTooltipText = Constants.Tooltips.CURRENCY;
    public totalSkuSizeTooltipText = Constants.Tooltips.TOTAL_SKU_SIZE;
    public dailyFrequencySelected: boolean;
    public pricePoints: Array<PricePer> = Constants.pricePoints;
    public volumeOptions: Array<VolumeUnits> = Constants.volumeOptions;
    public weightOptions: Array<WeightUnits> = Constants.weightOptions;
    public minDate: Date;
    public maxDate: Date;
    public maxSelectableDays = 0;
    public firstSeenPriceLower = 0;
    public firstSeenPriceUpper = 0;
    public lastSeenPriceLower = 0;
    public lastSeenPriceUpper = 0;
    public numberZero = 0;
    public numberFiftyThousand = 50000;
    public numberFiveThousand = 5000;

    public volumeChecked = false;
    public countChecked = false;
    public weightChecked = false;

    public fromVolume = 0;
    public toVolume = 50000;
    public fromCount = 0;
    public toCount = 5000;
    public fromWeight = 0;
    public toWeight = 50000;
    public defaultVolume: VolumeUnits = 'ml';
    public defaultWeight: WeightUnits = 'g';

    public volumeSelected = this.defaultVolume;
    public oldVolumeSelected: string = this.defaultVolume;

    public weightSelected = this.defaultWeight;
    public oldWeightSelected: string = this.defaultWeight;

    public dateRange = new FormGroup({
        startDate: new FormControl(),
        endDate: new FormControl(),
    });

    public isValidFirstSeenPriceRange = true;
    public isValidLastSeenPriceRange = true;
    public isValidVolumeRange = true;
    public isValidWeightRange = true;
    public isValidCountRange = true;
    public isValidMinDate = true;
    public isValidMaxDate = true;
    private readonly invalidStyleClassName = 'e-invalid-value';

    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private configureDataSubscription: Subscription;
    private skuMetadataSubscription: Subscription;

    private readonly regexPositiveNumberWithOrWithoutDecimals: RegExp = new RegExp('^([0-9]+(?:[\\.\\,][0-9]+)?)$');
    private readonly regexPositiveNumbersOnlywithoutDecimls: RegExp = new RegExp(/^[0-9]+$/);

    frmControlVolume = new FormControl();
    frmControlWeight = new FormControl();

    constructor(public dataExtractionStore: Store<DataExtractionState>, private skuMetadataStore: Store<SkuMetadataState>) {
        this.skuMetadataStore.dispatch(fetchSkuMetadata());

        this.minDate = moment().utc().subtract(Constants.DatePicker.MAX_ALLOWED_YEARS_AGO, 'years').startOf('year').toDate();

        this.skuMetadataSubscription = this.skuMetadataStore.pipe(select(getSkuMetadataStateFeature)).subscribe((skuMetadata) => {
            if (skuMetadata.lastUpdated) {
                this.maxDate = skuMetadata.lastUpdated;
                return;
            }
            this.maxDate = moment().utc().subtract(Constants.DatePicker.DEFAULT_MAX_DATE_DAYS_AGO, 'days').toDate();
        });
    }

    public ngOnInit() {
        this.configureDataSubscription = this.dataExtractionStore
            .pipe(select(getSelectedConfigureData))
            .subscribe((configuredDataFromStore: ConfigureData) => {
                this.configureData = configuredDataFromStore;
                this.dailyFrequencySelected = configuredDataFromStore.frequency === 'Daily';

                this.updateOrResetDateRange(configuredDataFromStore);
                this.updateOrResetFirstSeenPriceRange(configuredDataFromStore);
                this.updateOrResetLastSeenPriceRange(configuredDataFromStore);

                this.updateOrResetVolumeRange(configuredDataFromStore);
                this.updateOrResetWeightRange(configuredDataFromStore);
                this.updateOrResetCountRange(configuredDataFromStore);
            });

        this.maxSelectableDays = this.getMaxSelectableDays();

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            let firstSeenPriceRangeValue: MinMaxRange | null;
            let lastSeenPriceRangeValue: MinMaxRange | null;
            let dateRangeValue: DateRange | null;
            let volumeRangeValue: TotalSKUSizeRange | null;
            let weightRangeValue: TotalSKUSizeRange | null;
            let countRangeValue: TotalSKUSizeRange | null;

            if (
                (!this.firstSeenPriceLower || this.firstSeenPriceLower === this.numberZero) &&
                (!this.firstSeenPriceUpper || this.firstSeenPriceUpper === this.numberZero)
            ) {
                firstSeenPriceRangeValue = null;
            } else {
                firstSeenPriceRangeValue = new MinMaxRange(this.firstSeenPriceLower, this.firstSeenPriceUpper);
            }

            if (
                (!this.lastSeenPriceLower || this.lastSeenPriceLower === this.numberZero) &&
                (!this.lastSeenPriceUpper || this.lastSeenPriceUpper === this.numberZero)
            ) {
                lastSeenPriceRangeValue = null;
            } else {
                lastSeenPriceRangeValue = new MinMaxRange(this.lastSeenPriceLower, this.lastSeenPriceUpper);
            }

            const startDate = this.dateRange.controls.startDate.value;
            const endDate = this.dateRange.controls.endDate.value;
            if (startDate && endDate) {
                dateRangeValue = new DateRange(startDate, endDate);
            } else {
                dateRangeValue = null;
            }

            if ((!this.fromVolume || this.fromVolume < this.numberZero) && (!this.toVolume || this.toVolume > this.numberFiftyThousand)) {
                volumeRangeValue = null;
            } else {
                volumeRangeValue = this.volumeChecked
                    ? new TotalSKUSizeRange(this.volumeChecked, this.volumeSelected, this.fromVolume, this.toVolume)
                    : null;
            }

            if ((!this.fromWeight || this.fromWeight < this.numberZero) && (!this.toWeight || this.toWeight > this.numberFiftyThousand)) {
                weightRangeValue = null;
            } else {
                weightRangeValue = this.weightChecked
                    ? new TotalSKUSizeRange(this.weightChecked, this.weightSelected, this.fromWeight, this.toWeight)
                    : null;
            }

            if ((!this.fromCount || this.fromCount < this.numberZero) && (!this.toCount || this.toCount > this.numberFiveThousand)) {
                countRangeValue = null;
            } else {
                countRangeValue = this.countChecked ? new TotalSKUSizeRange(this.countChecked, null, this.fromCount, this.toCount) : null;
            }

            this.dataExtractionStore.dispatch(addFirstSeenPriceRangeSelection({ firstSeenPriceRange: firstSeenPriceRangeValue }));
            this.dataExtractionStore.dispatch(addLastSeenPriceRangeSelection({ lastSeenPriceRange: lastSeenPriceRangeValue }));
            this.dataExtractionStore.dispatch(addDateRangeSelection({ dateRange: dateRangeValue }));

            this.dataExtractionStore.dispatch(addVolumeRangeSelection({ volumeRange: volumeRangeValue }));
            this.dataExtractionStore.dispatch(addWeightRangeSelection({ weightRange: weightRangeValue }));
            this.dataExtractionStore.dispatch(addCountRangeSelection({ countRange: countRangeValue }));

            const configureData = {
                ...this.configureData,
                firstSeenPriceRange: firstSeenPriceRangeValue,
                lastSeenPriceRange: lastSeenPriceRangeValue,
                startDate: this.dateRange.controls.startDate.value ?? null,
                endDate: this.dateRange.controls.endDate.value ?? null,
                volumeRange: volumeRangeValue,
                weightRange: weightRangeValue,
                countRange: countRangeValue,
            };
            this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
            this.dataExtractionStore.dispatch(clearFilters());

            this.dataExtractionStore.dispatch(getCountries());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.dataExtractionStore.pipe(select(getSelectedConfigureData)).subscribe((configuredDataFromStore: ConfigureData) => {
                    this.updateOrResetDateRange(configuredDataFromStore);
                    this.updateOrResetFirstSeenPriceRange(configuredDataFromStore);
                    this.updateOrResetLastSeenPriceRange(configuredDataFromStore);

                    this.updateOrResetVolumeRange(configuredDataFromStore);
                    this.updateOrResetWeightRange(configuredDataFromStore);
                    this.updateOrResetCountRange(configuredDataFromStore);

                    this.showApplyButtonChange.emit(true);
                });
            });
        }
    }

    public validateFirstSeenPriceRange() {
        this.isValidFirstSeenPriceRange = this.handleValidation(this.firstSeenPriceLower, this.firstSeenPriceUpper, true);

        const minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('firstSeenPriceLower');
        const maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('firstSeenPriceUpper');
        this.updateStyleForFirstSeenPriceRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public validateLastSeenPriceRange() {
        this.isValidLastSeenPriceRange = this.handleValidation(this.lastSeenPriceLower, this.lastSeenPriceUpper, true);

        const minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('lastSeenPriceLower');
        const maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('lastSeenPriceUpper');
        this.updateStyleForLastSeenPriceRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public validateVolumeRange() {
        this.isValidVolumeRange = this.handleValidation(this.fromVolume, this.toVolume, true);

        const minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromVolume');
        const maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toVolume');
        this.updateStyleForVolumeRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public validateWeightRange() {
        this.isValidWeightRange = this.handleValidation(this.fromWeight, this.toWeight, true);

        const minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromWeight');
        const maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toWeight');
        this.updateStyleForWeightRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public validateCountRange() {
        this.isValidCountRange = this.handleValidation(this.fromCount, this.toCount, false);

        const minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromCount');
        const maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toCount');
        this.updateStyleForCountRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public checkCancelAndFilterButton() {
        this.checkFiltersAndEnableDisableApplyButton();
    }

    handleValidation(minValue: any, maxValue: any, validateWithDecimal: boolean) {
        let isValid = false;
        const customRegEx = validateWithDecimal ? this.regexPositiveNumberWithOrWithoutDecimals : this.regexPositiveNumbersOnlywithoutDecimls;

        // if both are null or undefined then it will be valid
        if (this.isNullOrUndefined(minValue) && this.isNullOrUndefined(maxValue)) {
            isValid = true;
        }

        // if both have values then it both should be positive values with or without decimal and range should be validated
        if (!this.isNullOrUndefined(minValue) && !this.isNullOrUndefined(maxValue)) {
            if (customRegEx.test(minValue) && customRegEx.test(maxValue) && Number(minValue) <= Number(maxValue)) {
                isValid = true;
            }
        }
        return isValid;
    }

    private updateStyleForFirstSeenPriceRangeElement(minElement: HTMLInputElement, maxElement: HTMLInputElement) {
        if (this.isValidFirstSeenPriceRange) {
            minElement.classList.remove(this.invalidStyleClassName);
            maxElement.classList.remove(this.invalidStyleClassName);
        } else {
            minElement.classList.add(this.invalidStyleClassName);
            maxElement.classList.add(this.invalidStyleClassName);
        }
    }

    private updateStyleForLastSeenPriceRangeElement(minElement: HTMLInputElement, maxElement: HTMLInputElement) {
        if (this.isValidLastSeenPriceRange) {
            minElement.classList.remove(this.invalidStyleClassName);
            maxElement.classList.remove(this.invalidStyleClassName);
        } else {
            minElement.classList.add(this.invalidStyleClassName);
            maxElement.classList.add(this.invalidStyleClassName);
        }
    }

    private updateStyleForVolumeRangeElement(minElement: HTMLInputElement, maxElement: HTMLInputElement) {
        if (this.isValidVolumeRange) {
            minElement.classList.remove(this.invalidStyleClassName);
            maxElement.classList.remove(this.invalidStyleClassName);
        } else {
            minElement.classList.add(this.invalidStyleClassName);
            maxElement.classList.add(this.invalidStyleClassName);
        }
    }

    private updateStyleForWeightRangeElement(minElement: HTMLInputElement, maxElement: HTMLInputElement) {
        if (this.isValidWeightRange) {
            minElement.classList.remove(this.invalidStyleClassName);
            maxElement.classList.remove(this.invalidStyleClassName);
        } else {
            minElement.classList.add(this.invalidStyleClassName);
            maxElement.classList.add(this.invalidStyleClassName);
        }
    }

    private updateStyleForCountRangeElement(minElement: HTMLInputElement, maxElement: HTMLInputElement) {
        if (this.isValidCountRange) {
            minElement.classList.remove(this.invalidStyleClassName);
            maxElement.classList.remove(this.invalidStyleClassName);
        } else {
            minElement.classList.add(this.invalidStyleClassName);
            maxElement.classList.add(this.invalidStyleClassName);
        }
    }

    private checkFiltersAndEnableDisableApplyButton() {
        if (
            this.isValidFirstSeenPriceRange &&
            this.isValidLastSeenPriceRange &&
            this.isValidVolumeRange &&
            this.isValidWeightRange &&
            this.isValidCountRange &&
            this.isValidMinDate &&
            this.isValidMaxDate
        ) {
            this.showApplyButtonChange.emit(true);
        } else {
            this.showApplyButtonChange.emit(false);
        }
    }

    public isNullOrUndefined(val: any): boolean {
        if (val === null || val === undefined) {
            return true;
        } else {
            return false;
        }
    }

    public updateOrResetDateRange(configuredDataFromStore: ConfigureData) {
        this.dateRange.controls.startDate.setValue(configuredDataFromStore?.startDate);
        this.dateRange.controls.endDate.setValue(configuredDataFromStore?.endDate);

        if (!this.dateRange.controls.startDate.value || !this.dateRange.controls.endDate.value) {
            this.selectDefaultDateRange();
        }
    }

    public updateOrResetFirstSeenPriceRange(configuredDataFromStore: ConfigureData) {
        this.firstSeenPriceLower = this.isNullOrUndefined(configuredDataFromStore?.firstSeenPriceRange?.min)
            ? this.numberZero
            : Number(configuredDataFromStore.firstSeenPriceRange?.min);
        this.firstSeenPriceUpper = this.isNullOrUndefined(configuredDataFromStore?.firstSeenPriceRange?.max)
            ? this.numberZero
            : Number(configuredDataFromStore.firstSeenPriceRange?.max);
    }

    public updateOrResetLastSeenPriceRange(configuredDataFromStore: ConfigureData) {
        this.lastSeenPriceLower = this.isNullOrUndefined(configuredDataFromStore?.lastSeenPriceRange?.min)
            ? this.numberZero
            : Number(configuredDataFromStore.lastSeenPriceRange?.min);
        this.lastSeenPriceUpper = this.isNullOrUndefined(configuredDataFromStore?.lastSeenPriceRange?.max)
            ? this.numberZero
            : Number(configuredDataFromStore.lastSeenPriceRange?.max);
    }

    public updateOrResetVolumeRange(configuredDataFromStore: ConfigureData) {
        this.fromVolume = this.isNullOrUndefined(configuredDataFromStore?.volumeRange?.min)
            ? this.numberZero
            : Number(configuredDataFromStore.volumeRange?.min);

        this.toVolume = this.isNullOrUndefined(configuredDataFromStore?.volumeRange?.max)
            ? this.numberFiftyThousand
            : Number(configuredDataFromStore.volumeRange?.max);

        this.volumeChecked = configuredDataFromStore?.volumeRange || configuredDataFromStore?.volumeRange?.isSelected ? true : false;

        this.volumeSelected = this.isNullOrUndefined(configuredDataFromStore?.volumeRange?.measureOptions)
            ? this.defaultVolume
            : (configuredDataFromStore?.volumeRange?.measureOptions as VolumeUnits);
        this.frmControlVolume.setValue(this.volumeSelected);
    }

    public updateOrResetWeightRange(configuredDataFromStore: ConfigureData) {
        this.fromWeight = this.isNullOrUndefined(configuredDataFromStore?.weightRange?.min)
            ? this.numberZero
            : Number(configuredDataFromStore.weightRange?.min);

        this.toWeight = this.isNullOrUndefined(configuredDataFromStore?.weightRange?.max)
            ? this.numberFiftyThousand
            : Number(configuredDataFromStore.weightRange?.max);

        this.weightChecked = configuredDataFromStore?.weightRange || configuredDataFromStore?.weightRange?.isSelected ? true : false;

        this.weightSelected = this.isNullOrUndefined(configuredDataFromStore?.weightRange?.measureOptions)
            ? this.defaultWeight
            : (configuredDataFromStore?.weightRange?.measureOptions as WeightUnits);
        this.frmControlWeight.setValue(this.weightSelected);
    }

    public updateOrResetCountRange(configuredDataFromStore: ConfigureData) {
        this.fromCount = this.isNullOrUndefined(configuredDataFromStore?.countRange?.min)
            ? this.numberZero
            : Number(configuredDataFromStore.countRange?.min);

        this.toCount = this.isNullOrUndefined(configuredDataFromStore?.countRange?.max)
            ? this.numberFiveThousand
            : Number(configuredDataFromStore.countRange?.max);

        this.countChecked = configuredDataFromStore?.countRange || configuredDataFromStore?.countRange?.isSelected ? true : false;
    }

    public onVolumeReset() {
        this.fromVolume = this.numberZero;
        this.toVolume = this.numberFiftyThousand;
        this.volumeSelected = this.defaultVolume;
        this.oldVolumeSelected = this.defaultVolume;

        this.isValidVolumeRange = true;
        let minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromVolume');
        let maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toVolume');
        this.updateStyleForVolumeRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public onWeightReset() {
        this.fromWeight = this.numberZero;
        this.toWeight = this.numberFiftyThousand;
        this.weightSelected = this.defaultWeight;
        this.oldWeightSelected = this.defaultWeight;

        this.isValidWeightRange = true;
        let minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromWeight');
        let maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toWeight');
        this.updateStyleForWeightRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public onCountReset() {
        this.fromCount = this.numberZero;
        this.toCount = this.numberFiveThousand;

        this.isValidCountRange = true;
        let minElement: HTMLInputElement = <HTMLInputElement>document.getElementById('fromCount');
        let maxElement: HTMLInputElement = <HTMLInputElement>document.getElementById('toCount');
        this.updateStyleForCountRangeElement(minElement, maxElement);
        this.checkFiltersAndEnableDisableApplyButton();
    }

    public updateFrequency(frequency: Frequency): void {
        this.updateDateRange(undefined, undefined);
        this.configureData = { ...this.configureData, frequency: frequency };
        this.dailyFrequencySelected = frequency === 'Daily';
        this.maxSelectableDays = this.getMaxSelectableDays();
    }

    public onDateRangeChange(): void {
        this.updateDateRange(this.dateRange.controls.startDate.value, this.dateRange.controls.endDate.value);
    }

    public onVolumeSelectionChange(event: MatSelectChange) {
        this.frmControlVolume.valueChanges.pipe(startWith(this.frmControlVolume.value), pairwise()).subscribe(([old, value]) => {
            this.oldVolumeSelected = old;
            this.volumeSelected = value;
        });
        this.fromVolume = Number(convertMetric(this.fromVolume, this.oldVolumeSelected, this.volumeSelected));
        this.toVolume = Number(convertMetric(this.toVolume, this.oldVolumeSelected, this.volumeSelected));

        this.validateVolumeRange();
    }

    public onWeightSelectionChange(event: MatSelectChange) {
        this.frmControlWeight.valueChanges.pipe(startWith(this.frmControlWeight.value), pairwise()).subscribe(([old, value]) => {
            this.oldWeightSelected = old;
            this.weightSelected = value;
        });

        this.fromWeight = Number(convertMetric(this.fromWeight, this.oldWeightSelected, this.weightSelected));
        this.toWeight = Number(convertMetric(this.toWeight, this.oldWeightSelected, this.weightSelected));

        this.validateWeightRange();
    }

    private selectDefaultDateRange(): void {
        const defaultDateRange = DateRange.getDefaultDateRange(this.maxDate);

        this.dateRange.controls.startDate.setValue(defaultDateRange.startDate);
        this.dateRange.controls.endDate.setValue(defaultDateRange.endDate);

        this.updateDateRange(defaultDateRange.startDate!, defaultDateRange.endDate!);
    }

    private updateDateRange(startDate: Date | undefined, endDate: Date | undefined) {
        this.configureData = { ...this.configureData, startDate: startDate, endDate: endDate };
    }

    private getMaxSelectableDays(): number {
        const dateRangeLimit = Constants.DatePicker.DATE_RANGE_LIMITS.find((limit) => {
            return limit.frequency === this.configureData.frequency;
        });

        if (dateRangeLimit) {
            // this.skuCountMessage = dateRangeLimit.message;
            return dateRangeLimit.maxSelectableDays;
        }

        // this.skuCountMessage = '';

        return 0;
    }

    public ngOnDestroy(): void {
        this.configureDataSubscription.unsubscribe();
        this.skuMetadataSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        this.cancelClickedSubscription?.unsubscribe();
        this.showApplyButtonChange.emit(false);
    }
}
